import { useEffect, useRef, useState } from 'react';
import { testimonials } from '@/mocks/testimonials';

const TestimonialsSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            section.querySelectorAll('.reveal').forEach((el) => {
              el.classList.add('revealed');
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -360 : 360, behavior: 'smooth' });
    setTimeout(checkScroll, 400);
  };

  return (
    <section
      id="depoimentos"
      ref={sectionRef}
      className="relative py-20 md:py-28 bg-background-100 overflow-hidden"
    >
      {/* Decorative blobs */}
      <div className="absolute top-0 left-[-100px] w-[400px] h-[400px] rounded-full bg-accent-50/50" />
      <div className="absolute bottom-0 right-[-100px] w-[400px] h-[400px] rounded-full bg-primary-50/40" />

      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14">
            <div>
              <span className="reveal inline-flex items-center gap-2 font-label text-xs md:text-sm text-accent-600 tracking-widest uppercase mb-3">
                <span className="w-6 h-px bg-accent-400" />
                Depoimentos
              </span>
              <h2 className="reveal reveal-delay-1 font-display text-3xl md:text-5xl text-foreground-900 leading-tight">
                O que dizem sobre <span className="text-accent-600">nós</span>
              </h2>
            </div>

            <div className="reveal reveal-delay-2 flex items-center gap-2">
              <button
                onClick={() => scroll('left')}
                className={`w-10 h-10 rounded-full border border-background-300 flex items-center justify-center transition-all cursor-pointer ${
                  canScrollLeft
                    ? 'text-foreground-600 hover:border-accent-400 hover:text-accent-600'
                    : 'text-foreground-300 pointer-events-none'
                }`}
              >
                <i className="ri-arrow-left-line" />
              </button>
              <button
                onClick={() => scroll('right')}
                className={`w-10 h-10 rounded-full border border-background-300 flex items-center justify-center transition-all cursor-pointer ${
                  canScrollRight
                    ? 'text-foreground-600 hover:border-accent-400 hover:text-accent-600'
                    : 'text-foreground-300 pointer-events-none'
                }`}
              >
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>

          <div
            ref={scrollRef}
            onScroll={checkScroll}
            className="flex gap-5 overflow-x-auto scrollbar-hide pb-4 -mx-2 px-2"
          >
            {testimonials.map((t, index) => (
              <div
                key={t.name}
                className="reveal flex-shrink-0 w-[320px] md:w-[360px]"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className="glass-warm rounded-3xl p-6 md:p-8 h-full flex flex-col">
                  {/* Stars */}
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`ri-star-fill text-sm ${
                          i < t.rating ? 'text-accent-400' : 'text-background-300'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Quote */}
                  <div className="font-display text-5xl text-accent-200 leading-none mb-3">
                    &ldquo;
                  </div>
                  <p className="font-body text-sm md:text-base text-foreground-600 leading-relaxed flex-grow">
                    {t.text}
                  </p>

                  {/* Author */}
                  <div className="flex items-center gap-3 mt-6 pt-5 border-t border-background-200/50">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover bg-background-200"
                    />
                    <div>
                      <div className="font-heading text-sm font-semibold text-foreground-800">
                        {t.name}
                      </div>
                      <div className="font-label text-xs text-foreground-400">
                        {t.role}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;